// ===========================================
// Edit Invoice Dialog
// ===========================================
// Edits a draft invoice. Existing line items are summary strings (weeks aren't
// persisted per the combined-summary model), so they remain free-text editable.
// On top of that, an admin can ADD weeks-based lessons: pick the family's
// enrolments for the invoice's term, choose weeks, and append the resulting
// line items — priced and worded by the SERVER from the pricing matrix
// (POST …/billable-context/preview), grouping-aware — to the editable list.
// Once appended they are ordinary editable lines, like every line here.
//
// mode="amend" (2026-09-26): the SAME dialog corrects an already-SENT invoice
// (SENT/OVERDUE, no payment yet). It adds a required, FAMILY-FACING reason and
// an "email the family" choice, and drops the week picker (plan R12). The
// server is the real gate — its refusals (a live checkout, a payment recorded,
// nothing changed) render inline here.
//
// Money controls on a DRAFT too (2026-10-02, MNM: "when editing an invoice, add a
// product or discount"): the product picker, the school's saved discounts (draft
// only), a one-off discount, Remove on existing discounts, and live totals from the
// server — saved WITH the edit in one transaction. Plan:
// md/plan/invoice-edit-add-product-discount-2026-10-02.md.

import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Grid,
  CircularProgress,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormControlLabel,
  Chip,
  InputAdornment,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  useUpdateInvoice,
  useAmendInvoice,
  useAmendPreview,
  useEditPreview,
  useBillableContext,
  useWeekInvoicePreview,
} from '../../hooks/useInvoices';
import { useDiscountPreview, useVariableDiscountSettings } from '../../hooks/useDiscounts';
import EligibleDiscountChecklist from './EligibleDiscountChecklist';
import RegistrationCreditSection from './RegistrationCreditSection';
import { apiErrorCode, apiErrorDetails, apiErrorMessage } from '../../utils/apiError';
import { amendBannerText, checkoutLockMessage, type AmendBannerContext } from '../../utils/invoiceAmend';
import { formatInSchoolTz } from '../../utils/dateUtils';
import AmendProductPicker from './AmendProductPicker';
import AmendTotalsPreview from './AmendTotalsPreview';
import AmendExistingDiscounts from './AmendExistingDiscounts';
import AmendDiscountFields, {
  AmendDiscountValue,
  EMPTY_AMEND_DISCOUNT,
  amendDiscountIsValid,
  withCoverage,
} from './AmendDiscountFields';
import { appliedCoverageSuffix, coverageFromSchoolDefault, normaliseCoverage } from '../../utils/discountCoverage';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useInvoiceSettings } from '../../hooks/useAdminSettings';
import {
  Invoice,
  CreateInvoiceItemInput,
  InvoiceLineKind,
  WeekInvoicePreviewInput,
  AmendPreviewInput,
  EditPreviewInput,
} from '../../api/invoices.api';
import LineItemWeekRow from './LineItemWeekRow';
import LineKindSelect from './LineKindSelect';
import WeekItemsPreview from './WeekItemsPreview';
import {
  SelectedEnrollment,
  hasUnpricedBucket as selectionsHaveUnpricedBucket,
  overlongItems,
  seedSelection,
  toWeekSelectionPayload,
} from './lineItemWeekBuilder';

interface EditInvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  /** 'amend' corrects an already-sent invoice (reason + optional family email). */
  mode?: 'edit' | 'amend';
  /** The school's IANA zone, for the checkout-lock times in amend mode. */
  schoolTimeZone?: string;
  /**
   * Offer "Apply a discount" (and, on a draft, the saved discounts) — the viewer has
   * operations_pricing:write. Both modes since 2026-10-02. Default off.
   */
  canApplyDiscount?: boolean;
  /** Offer "Remove" on existing discounts (operations_pricing:delete). Both modes. Default off. */
  canRemoveDiscount?: boolean;
  /**
   * The viewer may READ pricing (operations_pricing:read), which the saved-discount
   * list needs: write does not imply read. Default off = the list is not fetched.
   */
  canReadPricing?: boolean;
  /**
   * The class (or student) whose window change sent the admin here, in amend mode (a sent
   * invoice) or edit mode (a draft, D-b 2026-10-01). Renders an info banner above the lines.
   * Nothing more (D2): the change stays free-form.
   */
  amendContext?: AmendBannerContext | null;
}

/** The amendment reason is shown to the family; the server caps it at 500. */
const REASON_MAX = 500;

interface InvoiceLineItem extends Omit<CreateInvoiceItemInput, 'id' | 'kind'> {
  /** React key only (a new line gets a random one). */
  id: string;
  /** #372: the SERVER's id of a line already on the invoice; absent on a new line. */
  existingId?: string;
  kind: InvoiceLineKind;
  /** #372: made by the system; the server keeps its kind, so the picker is disabled. */
  kindLocked: boolean;
  /**
   * #625: the line's product category — the stored snapshot, or the picked product's category
   * for a new line. ONLY for the coverage estimate on screen: never sent (the server stamps it).
   */
  productCategoryId?: string | null;
}

/**
 * The ONE projection from a form line to what the server takes (draft edit, amend and both
 * previews). ⚠️ productId/pricingPackageId MUST survive (billing invariant), and since #372 so do
 * the line's server `id` (stable line ids: a locked line keeps its kind) and its `kind`.
 */
function toItemInput(item: InvoiceLineItem): CreateInvoiceItemInput {
  const { existingId, description, quantity, unitPrice, productId, pricingPackageId, kind } = item;
  return {
    ...(existingId ? { id: existingId } : {}),
    description,
    quantity,
    unitPrice,
    productId,
    pricingPackageId,
    kind,
  };
}

export default function EditInvoiceDialog({
  open,
  onClose,
  invoice,
  mode = 'edit',
  schoolTimeZone = 'Australia/Sydney',
  canApplyDiscount = false,
  canRemoveDiscount = false,
  canReadPricing = false,
  amendContext = null,
}: EditInvoiceDialogProps) {
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [lineItems, setLineItems] = useState<InvoiceLineItem[]>([]);
  const [reason, setReason] = useState('');
  const [notifyParent, setNotifyParent] = useState(true);
  // Amend mode (2026-09-28): an optional one-off discount applied with the amendment.
  const [showDiscount, setShowDiscount] = useState(false);
  const [discount, setDiscount] = useState<AmendDiscountValue>(EMPTY_AMEND_DISCOUNT);
  // Existing discounts marked for removal with this amendment / edit.
  const [removedDiscountIds, setRemovedDiscountIds] = useState<string[]>([]);
  // Draft edit (2026-10-02): an optional INTERNAL note for the removal audit row,
  // and the school's saved/eligible discounts ticked to apply.
  const [removeReason, setRemoveReason] = useState('');
  const [applyDiscountIds, setApplyDiscountIds] = useState<string[]>([]);
  // #374: children whose Held registration fee comes off this DRAFT (pre-ticked by the section).
  const [registrationCreditIds, setRegistrationCreditIds] = useState<string[]>([]);

  // "Add lessons by week" sub-flow state.
  const [showWeekPicker, setShowWeekPicker] = useState(false);
  const [studentIds, setStudentIds] = useState<string[]>([]);
  const [selections, setSelections] = useState<Record<string, SelectedEnrollment>>({});
  const [addEnrollmentId, setAddEnrollmentId] = useState('');

  const updateMutation = useUpdateInvoice();
  const amendMutation = useAmendInvoice();
  const { data: invoiceSettings } = useInvoiceSettings();
  const grouping = invoiceSettings?.invoiceLineItemGrouping ?? 'BY_CLASS';

  const isDraft = invoice?.status === 'DRAFT';
  const isAmend = mode === 'amend';
  /** Fields are live for a DRAFT edit, and for an amendment. */
  const editable = isAmend || isDraft;
  /** Product picker, discounts and live totals — amend, and a draft edit (2026-10-02). */
  const showsMoneyControls = editable;
  /** The saved-discount list: draft edit only (Amend's is a separate decision, plan §6). */
  const offersSavedDiscounts = !isAmend && !!isDraft && canApplyDiscount && canReadPricing;
  const savedDiscounts = useDiscountPreview(invoice?.id ?? '', {
    enabled: open && offersSavedDiscounts,
  });
  // #625 D6: a typed discount starts from the school's default coverage.
  const discountSettings = useVariableDiscountSettings({ enabled: open && canApplyDiscount && canReadPricing });
  const schoolDefaultCoverage = discountSettings.isSuccess ? coverageFromSchoolDefault(discountSettings.data) : undefined;
  // /qa #625 L1: a discount started before the default arrived takes it when it does, unless the
  // office has changed it since (the catalogue form's late pre-fill).
  useEffect(() => {
    if (!discountSettings.data) return;
    const fresh = coverageFromSchoolDefault(discountSettings.data);
    setDiscount((d) => (d.coverageTouched ? d : { ...d, coverage: { ...fresh, coversCategoryIds: [...fresh.coversCategoryIds] } }));
  }, [discountSettings.data]);
  // No week picker when amending (plan R12): free-form line edits only.
  const canAddWeeks = !isAmend && !!isDraft && !!invoice?.familyId && !!invoice?.termId;
  const saving = isAmend ? amendMutation.isPending : updateMutation.isPending;

  const { data: context, isLoading: contextLoading } = useBillableContext(
    canAddWeeks && showWeekPicker ? invoice!.familyId : undefined,
    canAddWeeks && showWeekPicker ? (invoice!.termId ?? undefined) : undefined
  );

  // Initialize form when invoice changes
  useEffect(() => {
    if (invoice) {
      setDescription(invoice.description || '');
      setDueDate(invoice.dueDate.split('T')[0]);
      setLineItems(
        // ⚠️ productId and pricingPackageId MUST be carried through: updateInvoice
        // deletes and recreates every line item, so a field dropped here is NULLed
        // on save. Until 2026-09-24 productId was omitted, silently severing every
        // product line from reporting and inventory history on any edit.
        // #372: and the server id + kind (a locked line keeps its kind only by its id).
        invoice.InvoiceItem.map((item) => ({
          id: item.id,
          existingId: item.id,
          description: item.description,
          quantity: item.quantity,
          unitPrice: parseFloat(item.unitPrice),
          productId: item.productId ?? undefined,
          pricingPackageId: item.pricingPackageId ?? undefined,
          kind: item.kind ?? 'OTHER',
          kindLocked: item.kindLocked ?? false,
          productCategoryId: item.productCategoryId ?? null,
        }))
      );
      setShowWeekPicker(false);
      setStudentIds([]);
      setSelections({});
      setAddEnrollmentId('');
      setReason('');
      setNotifyParent(true);
      setShowDiscount(false);
      setDiscount(EMPTY_AMEND_DISCOUNT);
      setRemovedDiscountIds([]);
      setRemoveReason('');
      setApplyDiscountIds([]);
      setRegistrationCreditIds([]);
      setConfirmCancelCheckout(false);
      amendMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on a new invoice only
  }, [invoice]);

  // Distinct students with enrolments in this family+term.
  const students = useMemo(() => {
    const seen = new Map<string, string>();
    for (const e of context?.enrollments ?? []) {
      if (!seen.has(e.studentId)) seen.set(e.studentId, e.studentName);
    }
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [context]);

  // Auto-select the only student; otherwise the admin picks (one or more).
  useEffect(() => {
    if (students.length === 1) {
      setStudentIds([students[0].id]);
    } else {
      setStudentIds((prev) => prev.filter((id) => students.some((s) => s.id === id)));
    }
  }, [students]);

  // Deselecting a student removes their already-added (not-yet-applied) rows.
  useEffect(() => {
    setSelections((prev) => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(([, sel]) => studentIds.includes(sel.enrollment.studentId))
      );
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [studentIds]);

  const availableEnrollments = useMemo(
    () =>
      (context?.enrollments ?? []).filter(
        (e) => studentIds.includes(e.studentId) && !selections[e.enrollmentId]
      ),
    [context, selections, studentIds]
  );

  // C1 guard: any selected bucket with weeks but no price (no matrix price and no
  // custom price switched on) blocks adding them. The server refuses it too.
  const hasUnpricedBucket = selectionsHaveUnpricedBucket(Object.values(selections));

  // The SERVER prices and words the lines to append (weeks + starting week +
  // switched-on custom prices are all that is sent).
  const weekSelectionPayload = useMemo(
    () => toWeekSelectionPayload(Object.values(selections)),
    [selections]
  );
  const previewInput: WeekInvoicePreviewInput | null = useMemo(
    () =>
      canAddWeeks && showWeekPicker && weekSelectionPayload.length > 0 && !hasUnpricedBucket
        ? { familyId: invoice!.familyId, termId: invoice!.termId!, selections: weekSelectionPayload }
        : null,
    [canAddWeeks, showWeekPicker, weekSelectionPayload, hasUnpricedBucket, invoice]
  );
  const debouncedPreviewInput = useDebouncedValue(previewInput, 300);
  const preview = useWeekInvoicePreview(debouncedPreviewInput);
  // Current only when it answers exactly the selection on screen.
  const previewCurrent =
    previewInput !== null &&
    debouncedPreviewInput === previewInput &&
    !!preview.data &&
    !preview.isPlaceholderData &&
    !preview.isFetching &&
    !preview.isError;
  const pendingWeekItems: CreateInvoiceItemInput[] =
    previewInput !== null && preview.data ? preview.data.items : [];
  const startDates = previewCurrent ? preview.data?.startDates : undefined;
  // The backend rejects a description over 200 characters (a start date and a
  // merged summary make that reachable) — block adding such a line.
  const hasOverlongItem = overlongItems(pendingWeekItems).length > 0;

  const handleAddEnrollment = () => {
    const enrollment = context?.enrollments.find((e) => e.enrollmentId === addEnrollmentId);
    if (!enrollment) return;
    setSelections((prev) => ({
      ...prev,
      // termWeeks bounds a start-week click to weeks the picker actually shows.
      [enrollment.enrollmentId]: seedSelection(enrollment, context?.termWeeks ?? null),
    }));
    setAddEnrollmentId('');
  };

  const handleRemoveEnrollment = (enrollmentId: string) => {
    setSelections((prev) => {
      const next = { ...prev };
      delete next[enrollmentId];
      return next;
    });
  };

  // Append the built week items to the editable line-item list, then collapse.
  const handleApplyWeekItems = () => {
    if (!previewCurrent || pendingWeekItems.length === 0 || hasUnpricedBucket || hasOverlongItem) return;
    setLineItems((prev) => [
      ...prev,
      // Weeks the server priced are tuition (the admin may still change a typed line's kind).
      ...pendingWeekItems.map((i) => ({ id: crypto.randomUUID(), ...i, kind: 'TUITION' as const, kindLocked: false })),
    ]);
    setSelections({});
    setAddEnrollmentId('');
    setShowWeekPicker(false);
  };

  /**
   * The discount fields a save (or preview) carries — each ONLY when set, so a plain
   * line edit sends exactly what it always did. Shared by both modes.
   */
  const discountFields = () => ({
    ...(removedDiscountIds.length > 0 ? { removeDiscountIds: removedDiscountIds } : {}),
    ...(showDiscount
      ? {
          discount: {
            name: discount.name.trim(),
            value: parseFloat(discount.value),
            isPercent: discount.isPercent,
            saveToCustomDiscounts: discount.saveToCustomDiscounts,
            // #625: sent whole, and only when the admin changed it (/qa phase 5 L2): untouched, the
            // server applies the school default.
            ...(discount.coverageTouched ? { coverage: normaliseCoverage(discount.coverage) } : {}),
          },
        }
      : {}),
  });

  const handleUpdate = () => {
    if (!invoice || lineItems.length === 0) return;
    if (showDiscount && !amendDiscountIsValid(discount)) return;

    const validItems = lineItems.filter(
      (item) => item.description.trim() && item.quantity > 0 && item.unitPrice > 0
    );
    if (validItems.length === 0) return;

    const dueDateISO = new Date(dueDate).toISOString();

    updateMutation.mutate(
      {
        id: invoice.id,
        data: {
          description: description || null,
          dueDate: dueDateISO,
          // ⚠️ productId/pricingPackageId are forwarded deliberately — see the note
          // on the initialising map above. Narrowing this projection re-breaks the FK.
          items: validItems.map(toItemInput),
          ...(applyDiscountIds.length > 0 ? { applyDiscountIds } : {}),
          ...(registrationCreditIds.length > 0 ? { addRegistrationCreditStudentIds: registrationCreditIds } : {}),
          ...discountFields(),
          ...(removedDiscountIds.length > 0 && removeReason.trim()
            ? { removeDiscountReason: removeReason.trim() }
            : {}),
        },
      },
      { onSuccess: () => onClose() }
    );
  };

  const handleAmend = (opts: { cancelCheckout?: boolean } = {}) => {
    if (!invoice || !reason.trim() || !dueDate) return;
    if (showDiscount && !amendDiscountIsValid(discount)) return;
    const validItems = lineItems.filter(
      (item) => item.description.trim() && item.quantity > 0 && item.unitPrice > 0
    );
    if (validItems.length === 0) return;

    amendMutation.mutate(
      {
        id: invoice.id,
        data: {
          description: description || null,
          dueDate: new Date(dueDate).toISOString(),
          // ⚠️ Same projection as handleUpdate: productId/pricingPackageId MUST survive.
          items: validItems.map(toItemInput),
          reason: reason.trim(),
          notifyParent,
          // Stops two admins silently overwriting each other (409 STALE_INVOICE).
          expectedAmendedCount: invoice.amendedCount ?? 0,
          ...(opts.cancelCheckout ? { cancelCheckout: true } : {}),
          ...discountFields(),
        },
      },
      { onSuccess: () => onClose() }
    );
  };

  // Phase 5 override: expire the family's live checkout, then amend.
  const [confirmCancelCheckout, setConfirmCancelCheckout] = useState(false);

  /** The server's refusal, worded for the admin. */
  const amendErrorText = (): string => {
    const error = amendMutation.error;
    switch (apiErrorCode(error)) {
      case 'CHECKOUT_IN_PROGRESS': {
        const d = apiErrorDetails<{ checkoutStartedAt?: string; checkoutExpiresAt?: string }>(error) ?? {};
        return checkoutLockMessage(
          { checkoutStartedAt: d.checkoutStartedAt ?? null, checkoutExpiresAt: d.checkoutExpiresAt ?? null },
          schoolTimeZone
        );
      }
      case 'PAYMENT_RECORDED':
        return 'A payment has been recorded against this invoice since you opened it, so it can no longer be amended.';
      case 'NO_CHANGES':
        return 'Nothing was changed. Edit a line, the description or the due date before saving.';
      case 'STALE_INVOICE':
        return 'Someone else amended this invoice after you opened it. Close this and reopen the invoice to see the latest version.';
      case 'PAYMENT_PROCESSING':
        return 'The family has just paid this invoice online, so it can no longer be amended.';
      default:
        return apiErrorMessage(error, 'Failed to amend invoice');
    }
  };

  const handleClose = () => onClose();

  const handleAddLineItem = () => {
    setLineItems([
      ...lineItems,
      { id: crypto.randomUUID(), description: '', quantity: 1, unitPrice: 0, kind: 'OTHER', kindLocked: false },
    ]);
  };

  const handleRemoveLineItem = (id: string) => {
    if (lineItems.length > 1) setLineItems(lineItems.filter((item) => item.id !== id));
  };

  const handleLineItemChange = (
    id: string,
    field: 'description' | 'quantity' | 'unitPrice' | 'kind',
    value: string | number
  ) => {
    setLineItems(lineItems.map((item) => (item.id === id ? { ...item, [field]: value } : item)));
  };

  const calculateTotal = () =>
    lineItems.reduce((sum, item) => sum + (item.quantity || 0) * (item.unitPrice || 0), 0);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount);

  // A line item with content (a description) but a zero/negative price or qty is
  // "incomplete" — e.g. an appended lesson whose matrix price was null, or an
  // existing item edited to $0. Block + warn (don't SILENTLY DROP it on save, the
  // old behaviour, which could quietly erase a real line). Mirrors CreateInvoiceDialog.
  const incompleteLineItems = lineItems.filter(
    (item) => item.description.trim() !== '' && (item.quantity <= 0 || item.unitPrice <= 0)
  );
  const hasIncompleteLineItem = incompleteLineItems.length > 0;

  // ---- Live totals (amend 2026-09-28; draft edit 2026-10-02) ----
  // The server runs the real amendment / edit and rolls it back; the browser only
  // asks. No preview while the form cannot be priced — say what is missing instead.
  // ⚠️ In BOTH modes: without it an incomplete line would be silently left out of the
  // preview below and the total shown would be wrong (plan review round 1, M3).
  const moneyWaitingFor: string | null = !showsMoneyControls
    ? null
    : hasIncompleteLineItem
      ? 'A line needs a description, a quantity and a price above $0 before the new total can be worked out.'
      : !dueDate
        ? 'Enter a due date to see the new total.'
        : showDiscount && !amendDiscountIsValid(discount)
          ? 'Finish the discount (a label and an amount) to see its effect.'
          : null;
  /** The money inputs both previews share; null while the form cannot be previewed. */
  const previewBase = useMemo(() => {
    if (!showsMoneyControls || !open || !invoice || moneyWaitingFor) return null;
    const items = lineItems
      .filter((item) => item.description.trim() && item.quantity > 0 && item.unitPrice > 0)
      .map(toItemInput);
    if (items.length === 0) return null;
    return {
      description: description || null,
      dueDate: new Date(dueDate).toISOString(),
      items,
      ...discountFields(),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- discountFields reads the state listed here
  }, [
    showsMoneyControls,
    open,
    invoice,
    moneyWaitingFor,
    lineItems,
    description,
    dueDate,
    showDiscount,
    discount,
    removedDiscountIds,
  ]);
  const amendPreviewInput: AmendPreviewInput | null = useMemo(
    () =>
      isAmend && previewBase && invoice
        ? { ...previewBase, expectedAmendedCount: invoice.amendedCount ?? 0 }
        : null,
    [isAmend, previewBase, invoice]
  );
  const editPreviewInput: EditPreviewInput | null = useMemo(
    () =>
      !isAmend && isDraft && previewBase
        ? {
            ...previewBase,
            ...(applyDiscountIds.length > 0 ? { applyDiscountIds } : {}),
            ...(registrationCreditIds.length > 0 ? { addRegistrationCreditStudentIds: registrationCreditIds } : {}),
          }
        : null,
    [isAmend, isDraft, previewBase, applyDiscountIds, registrationCreditIds]
  );
  const debouncedAmendPreviewInput = useDebouncedValue(amendPreviewInput, 500);
  const debouncedEditPreviewInput = useDebouncedValue(editPreviewInput, 500);
  const amendPreview = useAmendPreview(invoice?.id ?? '', debouncedAmendPreviewInput);
  const editPreview = useEditPreview(invoice?.id ?? '', debouncedEditPreviewInput);
  // The one the dialog shows. Current only when it answers exactly the form on screen.
  const totalsPreview = isAmend ? amendPreview : editPreview;
  const totalsPreviewUpdating = isAmend
    ? amendPreviewInput !== null &&
      (debouncedAmendPreviewInput !== amendPreviewInput || amendPreview.isFetching)
    : editPreviewInput !== null &&
      (debouncedEditPreviewInput !== editPreviewInput || editPreview.isFetching);
  /** Discounts this save would ADD (not on the invoice before), from the live preview. */
  const newPreviewDiscounts =
    totalsPreviewUpdating || !totalsPreview.data
      ? []
      : totalsPreview.data.after.discounts.filter(
          (d) => !totalsPreview.data!.before.discounts.some((b) => b.id === d.id)
        );

  const isValid =
    lineItems.some((item) => item.description.trim() && item.quantity > 0 && item.unitPrice > 0) &&
    !hasIncompleteLineItem;

  if (!invoice) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isAmend ? 'Amend Invoice' : 'Edit Invoice'} - {invoice.invoiceNumber}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {!isAmend && invoice.status !== 'DRAFT' && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              Only draft invoices can be edited. This invoice has been sent.
            </Alert>
          )}
          {amendContext && (
            <Alert severity="info" sx={{ mb: 1 }} data-testid="amend-context-banner">
              {amendBannerText(
                amendContext,
                invoice.sentAt
                  ? formatInSchoolTz(invoice.sentAt, schoolTimeZone, {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })
                  : null,
                invoice.Term?.name,
                invoice.status === 'DRAFT'
              )}
            </Alert>
          )}
          {isAmend && (
            <Alert severity="info" sx={{ mb: 1 }}>
              This invoice has already been sent. Amending it keeps the same invoice number and records the
              change in the invoice&apos;s history.
            </Alert>
          )}

          {/* Family (read-only) */}
          <TextField fullWidth label="Family" value={invoice.Family.name} disabled />

          {/* Term (read-only) */}
          <TextField fullWidth label="Term" value={invoice.Term?.name || 'None'} disabled />

          {/* Description */}
          <TextField
            fullWidth
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={!editable}
          />

          {/* Due Date */}
          <TextField
            fullWidth
            type="date"
            label="Due Date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={!editable}
          />

          <Divider sx={{ my: 1 }} />

          {/* Line Items */}
          <Box>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                Line Items
              </Typography>
              {editable && (
                <Button size="small" startIcon={<AddIcon />} onClick={handleAddLineItem}>
                  Add Item
                </Button>
              )}
            </Box>

            {lineItems.map((item) => (
              <Grid container spacing={1} key={item.id} sx={{ mb: 1, alignItems: 'center' }}>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    value={item.description}
                    onChange={(e) => handleLineItemChange(item.id, 'description', e.target.value)}
                    disabled={!editable}
                    // A catalogue product line (2026-09-28): it stays linked to the
                    // product (reporting, stock) whatever its description says.
                    InputProps={
                      item.productId
                        ? {
                            endAdornment: (
                              <InputAdornment position="end">
                                <Chip size="small" label="Product" color="secondary" variant="outlined" />
                              </InputAdornment>
                            ),
                          }
                        : undefined
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  {/* #372: what the line is for (always shown; locked on a system-made line). */}
                  <LineKindSelect
                    value={item.kind}
                    locked={item.kindLocked}
                    disabled={!editable}
                    onChange={(kind) => handleLineItemChange(item.id, 'kind', kind)}
                  />
                </Grid>
                <Grid item xs={4} sm={2}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Qty"
                    value={item.quantity}
                    onChange={(e) =>
                      handleLineItemChange(item.id, 'quantity', parseInt(e.target.value) || 0)
                    }
                    inputProps={{ min: 1 }}
                    disabled={!editable}
                  />
                </Grid>
                <Grid item xs={4} sm={2}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Price ($)"
                    value={item.unitPrice || ''}
                    onChange={(e) =>
                      handleLineItemChange(item.id, 'unitPrice', parseFloat(e.target.value) || 0)
                    }
                    inputProps={{ min: 0, step: 0.01 }}
                    placeholder="0.00"
                    disabled={!editable}
                  />
                </Grid>
                <Grid item xs={3} sm={2}>
                  <Typography variant="body2" sx={{ textAlign: 'right' }}>
                    {formatCurrency((item.quantity || 0) * (item.unitPrice || 0))}
                  </Typography>
                </Grid>
                <Grid item xs={1}>
                  {editable && (
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveLineItem(item.id)}
                      disabled={lineItems.length === 1}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
            ))}

            {/* Total */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                mt: 2,
                pt: 1,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              {showsMoneyControls ? (
                // Amend AND draft edit (2026-10-02): the invoice total (discounts +
                // GST) is the server's figure below; this is only the lines added up,
                // so it must not also be called "Total" (Andrew, 2026-09-28: two
                // "totals" that did not match).
                <Typography variant="subtitle1">
                  Lines total (before discounts): {formatCurrency(calculateTotal())}
                </Typography>
              ) : (
                <Typography variant="h6">Total: {formatCurrency(calculateTotal())}</Typography>
              )}
            </Box>
          </Box>

          {showsMoneyControls && (
            <AmendExistingDiscounts
              discounts={invoice.InvoiceDiscount ?? []}
              previewDiscounts={totalsPreviewUpdating ? undefined : totalsPreview.data?.after.discounts}
              removedIds={removedDiscountIds}
              onToggleRemove={
                canRemoveDiscount
                  ? (discountId) =>
                      setRemovedDiscountIds((prev) =>
                        prev.includes(discountId) ? prev.filter((x) => x !== discountId) : [...prev, discountId]
                      )
                  : undefined
              }
            />
          )}

          {editable && hasIncompleteLineItem && (
            <Alert severity="warning">
              Every line item needs a description, a quantity, and a price above $0. Fix or remove the
              highlighted item{incompleteLineItems.length === 1 ? '' : 's'} before saving.
            </Alert>
          )}

          {!isAmend && isDraft && removedDiscountIds.length > 0 && (
            <TextField
              fullWidth
              size="small"
              label="Reason for removing (internal, optional)"
              value={removeReason}
              onChange={(e) => setRemoveReason(e.target.value.slice(0, 1000))}
              helperText="Kept in the discount history. The family never sees it."
            />
          )}

          {showsMoneyControls && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <AmendProductPicker
                onAdd={(line) =>
                  setLineItems((prev) => [
                    ...prev,
                    // A product line: the server stamps it PRODUCT and locks it.
                    { id: crypto.randomUUID(), ...line, kind: 'PRODUCT', kindLocked: true },
                  ])
                }
              />
              {offersSavedDiscounts && savedDiscounts.isError && (
                // Not the same as "none on offer" — say so (code review L5).
                <Typography variant="caption" color="text.secondary">
                  Your saved discounts couldn&apos;t be loaded. You can still apply a one-off discount below.
                </Typography>
              )}
              {offersSavedDiscounts &&
                savedDiscounts.data &&
                savedDiscounts.data.availableDiscounts.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Saved discounts
                    </Typography>
                    <EligibleDiscountChecklist
                      discounts={savedDiscounts.data.availableDiscounts}
                      selectedIds={applyDiscountIds}
                      onToggle={(discountId, checked) =>
                        setApplyDiscountIds((prev) =>
                          checked ? [...prev, discountId] : prev.filter((x) => x !== discountId)
                        )
                      }
                    />
                  </Box>
                )}
              {!isAmend && isDraft && invoice && (
                <RegistrationCreditSection
                  familyId={invoice.familyId}
                  invoiceId={invoice.id}
                  selectedIds={registrationCreditIds}
                  onChange={setRegistrationCreditIds}
                />
              )}
              {!isAmend && newPreviewDiscounts.length > 0 && (
                <Box data-testid="new-discounts">
                  <Typography variant="subtitle2">New discounts</Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {newPreviewDiscounts.map((d) => (
                      <Typography component="li" variant="body2" key={d.id ?? d.name}>
                        {d.name}
                        {d.isPercent ? ` (${Number(d.value)}%)` : ''}
                        {appliedCoverageSuffix(d)} −{formatCurrency(Number(d.calculatedAmount))}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
              {canApplyDiscount && (
                <Box>
                  {!showDiscount ? (
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setShowDiscount(true);
                        // /qa #625 L1: without pricing read the default never loads, so the
                        // "everything" on screen is sent as chosen, never swapped on the server
                        // for a default this user cannot see.
                        setDiscount(
                          schoolDefaultCoverage || canReadPricing
                            ? withCoverage(schoolDefaultCoverage)
                            : { ...withCoverage(undefined), coverageTouched: true }
                        );
                      }}
                    >
                      Apply a discount
                    </Button>
                  ) : (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2">Apply a discount</Typography>
                        <Button
                          size="small"
                          onClick={() => {
                            setShowDiscount(false);
                            setDiscount(EMPTY_AMEND_DISCOUNT);
                          }}
                        >
                          Remove
                        </Button>
                      </Box>
                      <AmendDiscountFields
                        value={discount}
                        onChange={setDiscount}
                        lines={lineItems.map((l) => ({
                          kind: l.kind,
                          productCategoryId: l.productCategoryId ?? null,
                          amount: (l.quantity || 0) * (l.unitPrice || 0),
                        }))}
                        alreadyOff={(invoice?.InvoiceDiscount ?? [])
                          .filter((d) => !removedDiscountIds.includes(d.id))
                          .reduce((sum, d) => sum + Number(d.calculatedAmount), 0)}
                        defaultCoverage={schoolDefaultCoverage}
                        canReadCategories={canReadPricing}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}

          {isAmend && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Divider sx={{ my: 1 }} />
              <AmendTotalsPreview
                currentTotal={invoice.total}
                data={amendPreview.data}
                updating={totalsPreviewUpdating}
                error={amendPreview.error}
                waitingFor={moneyWaitingFor}
              />
              <TextField
                fullWidth
                required
                multiline
                minRows={2}
                label="Reason (the family will see this)"
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, REASON_MAX))}
                helperText={`${reason.length}/${REASON_MAX} — shown in the email and on the family's invoice`}
              />
              <FormControlLabel
                control={
                  <Checkbox checked={notifyParent} onChange={(e) => setNotifyParent(e.target.checked)} />
                }
                label="Email the family about this change"
              />
            </Box>
          )}

          {/* ---- Add lessons by week (draft + has term) ---- */}
          {canAddWeeks && (
            <Box>
              <Divider sx={{ my: 1 }} />
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setShowWeekPicker((v) => !v)}
              >
                {showWeekPicker ? 'Hide week selector' : 'Add lessons by week'}
              </Button>

              <Collapse in={showWeekPicker} unmountOnExit>
                <Box sx={{ mt: 2 }}>
                  {contextLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={22} />
                    </Box>
                  )}
                  {context && context.enrollments.length === 0 && (
                    <Alert severity="info">No active enrolments for this family and term.</Alert>
                  )}

                  {/* Student picker — only for families with >1 student. */}
                  {students.length > 1 && (
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel>Students</InputLabel>
                      <Select
                        multiple
                        value={studentIds}
                        onChange={(e) =>
                          setStudentIds(
                            typeof e.target.value === 'string'
                              ? e.target.value.split(',')
                              : e.target.value
                          )
                        }
                        input={<OutlinedInput label="Students" />}
                        renderValue={(selected) =>
                          students
                            .filter((s) => selected.includes(s.id))
                            .map((s) => s.name)
                            .join(', ')
                        }
                      >
                        {students.map((s) => (
                          <MenuItem key={s.id} value={s.id}>
                            <Checkbox checked={studentIds.includes(s.id)} />
                            <ListItemText primary={s.name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {students.length > 1 && studentIds.length === 0 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Select one or more students to bill.
                    </Alert>
                  )}

                  {availableEnrollments.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                      <FormControl fullWidth size="small">
                        {/* Linked label: gives the select its accessible name (it had
                            none, so it could only be found as "the only combobox" —
                            untrue once the draft also shows the Product picker). */}
                        <InputLabel id="edit-invoice-add-lesson-label">Add a lesson</InputLabel>
                        <Select
                          labelId="edit-invoice-add-lesson-label"
                          value={addEnrollmentId}
                          label="Add a lesson"
                          onChange={(e: SelectChangeEvent) => setAddEnrollmentId(e.target.value)}
                        >
                          {availableEnrollments.map((e) => (
                            <MenuItem key={e.enrollmentId} value={e.enrollmentId}>
                              {e.studentName} — {e.lessonName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleAddEnrollment}
                        disabled={!addEnrollmentId}
                      >
                        Add
                      </Button>
                    </Box>
                  )}

                  {context &&
                    Object.values(selections).map((sel) => (
                      <LineItemWeekRow
                        key={sel.enrollment.enrollmentId}
                        enrollment={sel.enrollment}
                        termStartDate={context.termStartDate}
                        termEndDate={context.termEndDate}
                        value={sel}
                        onChange={(next) =>
                          setSelections((prev) => ({
                            ...prev,
                            [sel.enrollment.enrollmentId]: next,
                          }))
                        }
                        onRemove={() => handleRemoveEnrollment(sel.enrollment.enrollmentId)}
                        startDate={startDates?.[sel.enrollment.enrollmentId]}
                      />
                    ))}

                  {hasUnpricedBucket && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      One or more lessons have no price set. Enter a price per week before adding.
                    </Alert>
                  )}

                  <WeekItemsPreview
                    items={pendingWeekItems}
                    caption={grouping === 'BY_PROGRAM_LEVEL' ? 'grouped by program level' : 'one per class'}
                    updating={previewInput !== null && !previewCurrent && !preview.isError}
                  />
                  {preview.isError && previewInput !== null && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {(preview.error as Error)?.message || 'Could not price the selected weeks'}
                    </Alert>
                  )}

                  {pendingWeekItems.length > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                      <Button
                        variant="contained"
                        onClick={handleApplyWeekItems}
                        disabled={!previewCurrent || hasUnpricedBucket || hasOverlongItem}
                      >
                        Add {pendingWeekItems.length} line item
                        {pendingWeekItems.length === 1 ? '' : 's'} to invoice
                      </Button>
                    </Box>
                  )}
                </Box>
              </Collapse>
            </Box>
          )}

          {/* Draft edit's live totals (2026-10-02) — last, below the week picker, so
              the total sits at the bottom whatever was added above it. */}
          {!isAmend && isDraft && (
            <Box>
              <Divider sx={{ my: 1 }} />
              <AmendTotalsPreview
                currentTotal={invoice.total}
                data={editPreview.data}
                updating={totalsPreviewUpdating}
                error={editPreview.error}
                waitingFor={moneyWaitingFor}
              />
            </Box>
          )}

          {!isAmend && updateMutation.isError && (
            <Alert severity="error">
              {/* The server's reason (a discount no longer on offer, not on this
                  invoice, a permission) — never axios's "status code 400". */}
              {apiErrorMessage(updateMutation.error, 'Failed to update invoice')}
            </Alert>
          )}
          {isAmend && amendMutation.isError && (
            <Alert
              severity="error"
              action={
                apiErrorCode(amendMutation.error) === 'CHECKOUT_IN_PROGRESS' ? (
                  <Button color="inherit" size="small" onClick={() => setConfirmCancelCheckout(true)}>
                    Cancel their checkout and amend
                  </Button>
                ) : undefined
              }
            >
              {amendErrorText()}
            </Alert>
          )}
          {isAmend && confirmCancelCheckout && (
            <Alert
              severity="warning"
              action={
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button color="inherit" size="small" onClick={() => setConfirmCancelCheckout(false)}>
                    Keep waiting
                  </Button>
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setConfirmCancelCheckout(false);
                      handleAmend({ cancelCheckout: true });
                    }}
                  >
                    Cancel it and amend
                  </Button>
                </Box>
              }
            >
              The family will see &quot;session expired&quot; on the payment page and will need to start again at the
              new amount. If they finish paying first, the invoice can no longer be amended.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          {editable ? 'Cancel' : 'Close'}
        </Button>
        {editable && (
          <Button
            variant="contained"
            onClick={isAmend ? () => handleAmend() : handleUpdate}
            disabled={
              !isValid ||
              saving ||
              (showDiscount && !amendDiscountIsValid(discount)) ||
              (isAmend && (!reason.trim() || !dueDate))
            }
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {saving ? 'Saving...' : isAmend ? 'Save amendment' : 'Save Changes'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
